import React, { Component } from "react";
import { Redirect } from "react-router-dom";
import decode from "jwt-decode";
import debounce from "lodash.debounce";

// redux
import { connect } from "react-redux";
import { getUserRequest } from "../../redux/actions/users";
import {
  getBrowseRequest,
  setLikeRequest,
  setDislikeRequest,
  clearBrowseResults,
} from "../../redux/actions/browse";
import {
  updateSearchPreferencesRequest,
  updateFetchProfilesRequest,
} from "../../redux/actions/searchPreferences";
import { getBrowseDataFiltered } from "../../redux/selectors/browse";

// apollo
import { withApollo } from "react-apollo";
import queries from "../../apollo/queries/queries";
import Mutations from "../../apollo/mutations/mutations";

// modals
import ModamilyAppMatchModal from "../../components/modals/ModamilyAppMatchModal";

// common
import ModamilyAppNavBar from "../../components/common/ModamilyAppNavBar";
import ModamilyAppLoadingComponent from "../../components/common/ModamilyAppLoadingComponent";

// local
import BrowseCarousel from "./BrowseCarousel";

// styles
import styles from "./modamilyAppBrowse.scss";
import classNames from "classnames/bind";

const cx = classNames.bind(styles);

class ModamilyAppBrowse extends Component {
  constructor(props) {
    super(props);
    this.state = {
      user: null,
      getOnboardingStatus: false,
      getUser: false,
      getBrowse: false,
      users: [],
      usersDB: [],
      loading: false,
      showMatchModal: false,
      isLoading: false,
      hasMore: true,
      page: 1,
      error: "",
      limit: 10,
      lastSearchPreferences: null,
      shouldReloadUsers: false,
    };

    // Sets para mantener IDs de usuarios procesados (like/dislike) durante la sesión
    this.likedUserIds = new Set();
    this.dislikedUserIds = new Set();

    // TODO enable this once we change flex-direction column, this will allow us to have a infinite scroll.
    // Binds our scroll event handler
    window.onscroll = debounce(() => {
      const {
        loadUsers,
        state: { error, isLoading, hasMore, page, limit },
      } = this;
      if (error || isLoading || !hasMore) return;
      // Checks that the page has scrolled to the bottom
      let documentHeight = document.body.scrollHeight;
      let currentScroll = window.scrollY + window.innerHeight;
      // When the user is [modifier]px from the bottom, fire the event.
      let modifier = 100;
      if (currentScroll + modifier > documentHeight) {
        loadUsers(page + 1, limit);
      }
    }, 100);
  }

  static getDerivedStateFromProps(nextProps, prevState) {
    // Si las preferencias de búsqueda cambiaron, necesitamos recargar los usuarios
    const prevSearchPrefs = prevState.lastSearchPreferences;
    const nextSearchPrefs = nextProps.searchPreferences?.items;
    
    // Comparar preferencias clave que afectan la búsqueda
    const searchPrefsChanged = 
      !prevSearchPrefs ||
      prevSearchPrefs.minAge !== nextSearchPrefs?.minAge ||
      prevSearchPrefs.maxAge !== nextSearchPrefs?.maxAge ||
      prevSearchPrefs.minHeight !== nextSearchPrefs?.minHeight ||
      prevSearchPrefs.maxHeight !== nextSearchPrefs?.maxHeight ||
      prevSearchPrefs.minIncome !== nextSearchPrefs?.minIncome ||
      prevSearchPrefs.maxIncome !== nextSearchPrefs?.maxIncome ||
      prevSearchPrefs.country !== nextSearchPrefs?.country ||
      prevSearchPrefs.zipcode !== nextSearchPrefs?.zipcode ||
      prevSearchPrefs.distance !== nextSearchPrefs?.distance;
    
    if (searchPrefsChanged && nextProps.searchPreferences?.items) {
      // Las preferencias cambiaron, marcar para recargar
      return {
        ...prevState,
        lastSearchPreferences: { ...nextSearchPrefs },
        shouldReloadUsers: true,
      };
    }
    
    // Actualizar usuarios si hay nuevos datos
    if (nextProps.browseData.data.length >= prevState.users.length) {
      return {
        users: nextProps.browseData.data,
        lastSearchPreferences: nextSearchPrefs ? { ...nextSearchPrefs } : prevSearchPrefs,
      };
    }
    
    return null;
  }
  
  componentDidUpdate(prevProps, prevState) {
    // Si las preferencias cambiaron, recargar usuarios
    if (this.state.shouldReloadUsers) {
      this.setState({ shouldReloadUsers: false, users: [], page: 1, hasMore: true });
      this.loadUsers(1, this.state.limit);
    }
  }

  componentWillUnmount() {
    this.props.clearBrowseResults();
  }

  async componentDidMount() {
    let { client } = this.props;
    const { page, limit } = this.state;
    this.setState({ loading: true });
    // todo move to redux and use redux-persist
    let user = decode(localStorage.getItem("auth.token"));

    if (!user) {
      return <Redirect to="/" />;
    }
    this.setState({ user });

    // Cargar likes/dislikes existentes para filtrarlos del browse
    try {
      const likesResponse = await client.query({
        query: queries.getLikes,
      });
      if (likesResponse?.data?.getLikes) {
        const likesData = decode(likesResponse.data.getLikes);
        // userLiked: usuarios a los que el usuario actual les dio like
        // Estos deben filtrarse del browse
        if (likesData.userLiked && Array.isArray(likesData.userLiked)) {
          likesData.userLiked.forEach((user) => {
            const userId = user.id || user.userId;
            if (userId) {
              this.likedUserIds.add(userId);
            }
          });
        }
        // Nota: Los dislikes no vienen en getLikes, pero el backend debería filtrarlos
        // Si necesitamos filtrar dislikes también, tendríamos que hacer otra query
      }
    } catch (error) {
      console.error("Error loading likes for filtering:", error);
      // Continuar aunque falle la carga de likes
    }

    // filter & subscription
    const filtersAndSubscriptionResponse = await client.query({
      query: queries.getZipcodeCountryAndSubscription,
    });
    let filtersAndSubscription = this.handleFiltersAndSubscriptionResponse(
      filtersAndSubscriptionResponse,
    );
    // onboarding
    const onboardingStatus = await client.query({
      query: queries.getOnboardingStatus,
    });
    const onboardingData = this.handleOnboardingResponse(onboardingStatus.data);

    if (onboardingData.continueToBrowse) {
      // Obtener datos del usuario para usar como fallback solo para country si no hay preferencias
      const userData = onboardingData.userData || {};

      // Si no hay preferencias de búsqueda configuradas, usar solo el país del usuario como fallback
      // NO usar el zipcode del usuario si no está en las preferencias
      if (!filtersAndSubscription || !filtersAndSubscription.country) {
        filtersAndSubscription = {
          ...filtersAndSubscription,
          country: userData.country || user.country,
        };
      }

      // Asegurar que zipcode esté presente solo si viene de las preferencias
      // No usar zipcode del usuario como fallback
      if (filtersAndSubscription.zipCode || filtersAndSubscription.zipcode) {
        filtersAndSubscription = {
          ...filtersAndSubscription,
          zipcode:
            filtersAndSubscription.zipcode || filtersAndSubscription.zipCode,
        };
      }

      // Usar solo el zipcode de las preferencias, no del usuario
      // Si no hay zipcode en las preferencias, dejar undefined/null para que la búsqueda funcione solo con país
      const finalZipcode =
        filtersAndSubscription.zipcode ||
        filtersAndSubscription.zipCode ||
        null;
      const finalCountry =
        filtersAndSubscription.country || userData.country || user.country;

      // Guardar en state para usar en loadUsers
      this.setState({
        zipCode: finalZipcode,
        country: finalCountry,
        is_free:
          filtersAndSubscription.is_free || filtersAndSubscription.isFree || 0,
      });

      // browse
      const browseDataV2 = await client.query({
        query: queries.getUsersBrowse,
        variables: {
          page,
          limit,
          zipcode: finalZipcode,
          country: finalCountry,
          is_free:
            filtersAndSubscription.is_free ||
            filtersAndSubscription.isFree ||
            0,
        },
        // Forzar fetch desde el servidor para evitar caché de Apollo
        fetchPolicy: 'network-only',
      });
      this.handleBrowseResponse(browseDataV2.data);
    }

    this.setState({ loading: false });
  }

  loadUsers = (page, limit) => {
    this.setState({ isLoading: true }, async () => {
      let { client, searchPreferences } = this.props;
      let { users, zipCode, country, is_free, user } = this.state;

      // Obtener valores actualizados de las preferencias de búsqueda desde Redux
      const searchPrefs = searchPreferences?.items || {};
      
      // Usar valores de las preferencias actualizadas si están disponibles
      const finalZipCode = searchPrefs.zipcode || searchPrefs.zipCode || zipCode || null;
      const finalCountry = searchPrefs.country || country || user?.country;
      // Asegurar que is_free tenga un valor por defecto
      const finalIsFree = searchPrefs.is_free !== undefined ? searchPrefs.is_free : (is_free !== undefined ? is_free : 0);

      // Actualizar el state con los valores actualizados de las preferencias
      if (searchPrefs.zipcode || searchPrefs.zipCode) {
        this.setState({ zipCode: finalZipCode });
      }
      if (searchPrefs.country) {
        this.setState({ country: finalCountry });
      }
      if (searchPrefs.is_free !== undefined) {
        this.setState({ is_free: finalIsFree });
      }

      const browseDataV2 = await client.query({
        query: queries.getUsersBrowse,
        variables: {
          page,
          limit,
          zipcode: finalZipCode,
          country: finalCountry,
          is_free: finalIsFree,
        },
        // Forzar fetch desde el servidor para evitar caché de Apollo
        fetchPolicy: 'network-only',
      });
      const newUsers = this.handleBrowseResponse(browseDataV2.data);

      // Si es la primera página (recarga completa), reemplazar usuarios
      // Si es una página posterior, agregar a los existentes
      if (page === 1) {
        // Filtrar usuarios que ya fueron procesados (like/dislike)
        const filteredNewUsers = (newUsers || []).filter((u) => {
          const userId = u.id || u.userId;
          return (
            userId &&
            !this.likedUserIds.has(userId) &&
            !this.dislikedUserIds.has(userId)
          );
        });
        this.setState({
          users: filteredNewUsers,
          page,
          isLoading: false,
          hasMore: filteredNewUsers.length > 0,
        });
      } else {
        // Filtrar usuarios que ya fueron procesados (like/dislike) o que ya están en la lista
        const existingIds = new Set(users.map((u) => u.id || u.userId));
        const filteredNewUsers = (newUsers || []).filter((u) => {
          const userId = u.id || u.userId;
          return (
            userId &&
            !existingIds.has(userId) &&
            !this.likedUserIds.has(userId) &&
            !this.dislikedUserIds.has(userId)
          );
        });

        users = users.concat(filteredNewUsers);
        this.setState({
          users,
          page,
          isLoading: false,
          hasMore: filteredNewUsers.length > 0,
        });
      }
    });
  };

  handleFiltersAndSubscriptionResponse = (res) => {
    try {
      let response = decode(res.data.getZipcodeCountryAndSubscription);
      if (response.action === "success") {
        this.setState({ ...response.data });
        return {
          ...response.data,
        };
      }
      // Retornar objeto vacío en lugar de undefined
      return {};
    } catch (error) {
      console.log(error, res);
      // Retornar objeto vacío en lugar de undefined
      return {};
    }
  };

  handleBrowseResponse = (data) => {
    try {
      let response = decode(data.getUsersBrowse);
      if (response.action === "success") {
        // Filtrar usuarios que ya fueron procesados (like/dislike)
        const filteredData = (response.data || []).filter((u) => {
          const userId = u.id || u.userId;
          return (
            userId &&
            !this.likedUserIds.has(userId) &&
            !this.dislikedUserIds.has(userId)
          );
        });
        this.props.getBrowseRequest(filteredData);
        return filteredData;
      }
    } catch (error) {
      console.error(error, data);
      localStorage.clear();
    }
  };

  handleUserError = (error) => {
    console.error(error);
  };

  handleOnboardingError = (error) => {
    console.error(error);
  };

  handleBrowseError = (error) => {
    console.error(error);
  };

  handleOnboardingResponse = (data) => {
    let continueToBrowse = true;
    let userData = null;
    try {
      const response = decode(data.getOnboardingStatus);
      userData = response.data.user; // Guardar datos del usuario

      // TODO: if status === 1 and no country and zip code, redirect.
      if (response.data.user.onboardingStatus === 0) {
        continueToBrowse = false;
        this.props.history.push("/onboarding");
        // console.info("REDIRECT TO ONBOARDING");
      } else if (!response.data.user.phone) {
        window.location.href = "/phone-number";
      }

      // let keys = Object.keys(response.data);
      // for (let index = 0; index < keys.length; index++) {
      //   const key = keys[index];
      //   if (!response.data[key]) {
      //     this.props.history.push("/onboarding");
      //     continueToBrowse = false;
      //   }
      // }
    } catch (error) {
      continueToBrowse = false;
      console.log(error, data);
      localStorage.clear();
    }
    return { continueToBrowse, userData };
  };

  handleLike = async (val, user) => {
    try {
      const { client } = this.props;
      let { users } = this.state;
      this.setState({ likeLoading: true });
      const res = await client.mutate({
        mutation: Mutations.addRelationship,
        variables: {
          relationship: val,
          userb: user.id,
        },
      });
      let response = decode(res.data.addRelationship);
      if (response.action === "success") {
        this.handleExitFullProfile();
        this.props.setLikeRequest(user.id);
        // Agregar el ID del usuario al Set para evitar que vuelva a aparecer
        const userId = user.id || user.userId;
        if (userId) {
          this.likedUserIds.add(userId);
        }
        users = users.filter((u) => (u.id || u.userId) !== userId);
        if (response.data.likedBack) {
          this.setState({
            showMatchModal: response.data.likedBack,
            otherUser: user,
            users,
            likeLoading: false,
            fullProfile: false,
            fullProfileUser: null,
          });
        } else {
          this.setState({
            users,
            likeLoading: false,
            fullProfile: false,
            fullProfileUser: null,
          });
        }
      } else {
        this.setState({
          likeLoading: false,
          fullProfile: false,
          fullProfileUser: null,
        });
      }
    } catch (err) {
      console.error("error liking user", err);
    }
  };

  handleUnlike = async (val, user) => {
    const { client } = this.props;
    let { users } = this.state;
    this.setState({ dislikeLoading: true });
    const res = await client.mutate({
      mutation: Mutations.addRelationship,
      variables: {
        relationship: val,
        userb: user.id,
      },
    });
    let response = decode(res.data.addRelationship);
    if (response.action === "success") {
      this.handleExitFullProfile();
      this.props.setDislikeRequest(user.id);
      // Agregar el ID del usuario al Set para evitar que vuelva a aparecer
      const userId = user.id || user.userId;
      if (userId) {
        this.dislikedUserIds.add(userId);
      }
      users = users.filter((u) => (u.id || u.userId) !== userId);
      this.setState({
        dislikeLoading: false,
        fullProfile: false,
        fullProfileUser: null,
        users,
      });
    } else {
      this.setState({
        dislikeLoading: false,
        fullProfile: false,
        fullProfileUser: null,
      });
    }
  };

  refreshClick = () => {
    window.location.href = "/browse";
  };

  handleFullProfileModal = (user) => {
    this.setState({
      fullProfile: !!user,
      fullProfileUser: user,
    });
  };

  handleExitFullProfile = () => {
    this.setState({
      fullProfile: false,
      fullProfileUser: null,
    });
  };

  render = () => {
    let {
      fullProfile,
      fullProfileUser,
      user,
      users,
      loading,
      isLoading,
      likeLoading,
      dislikeLoading,
      showMatchModal,
    } = this.state;

    if (loading) {
      return <ModamilyAppLoadingComponent />;
    }
    return (
      <div className={cx("flexReset", "modamilyAppBrowse")}>
        <ModamilyAppNavBar type="inApp" currentLink="browse" />
        <div className={cx("flexReset", "modamilyAppBrowse-pageContainer")}>
          <BrowseCarousel
            users={users}
            refreshClick={this.refreshClick}
            viewFullProfile={this.handleFullProfileModal}
            onExitFullProfile={this.handleExitFullProfile}
            likeUser={this.handleLike}
            dislikeUser={this.handleUnlike}
            fullProfile={fullProfile}
            fullProfileUser={fullProfileUser}
            likeLoading={likeLoading}
            dislikeLoading={dislikeLoading}
          />
          {isLoading ? <ModamilyAppLoadingComponent /> : null}
          <ModamilyAppMatchModal
            key={this.state.otherUser}
            userMatchedWithImage={
              this.state.otherUser
                ? this.state.otherUser.images?.length > 0
                  ? this.state.otherUser.images[0]
                  : null
                : null
            }
            currentUserImage={
              user ? (user.images?.length > 0 ? user.images[0] : null) : null
            }
            active={showMatchModal}
          />
        </div>
      </div>
    );
  };
}

export default connect(
  ({ searchPreferences, browse }) => {
    return {
      browseData: getBrowseDataFiltered(browse.items, searchPreferences.items),
      searchPreferences: searchPreferences,
    };
  },
  {
    getUserRequest,
    updateSearchPreferencesRequest,
    getBrowseRequest,
    setLikeRequest,
    setDislikeRequest,
    updateFetchProfilesRequest,
    clearBrowseResults,
  },
)(withApollo(ModamilyAppBrowse));
